import { Component, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AgreementService } from 'src/app/admin/data-access/agreement.service';
import { Subscription, map, Observable } from 'rxjs';
import { Dialog } from "@angular/cdk/dialog";
import { faAdd, faChevronDown, faChevronUp, faEye, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { MatSnackBar } from "@angular/material/snack-bar";
import _ from "lodash";

interface OnInit {
  ngOnInit(): void;
}

interface AgreementList {
  agreement_id: number
  name: string;
  status: string;
  required: boolean;
  sequence: number;
  effective_date: string;
  retire_date: string;
  expanded: boolean;
  responses: number;
  createdate: string;
}

@Component({
  selector: 'agreementManagementList',
  templateUrl: './agreementManagementList.html',
  styleUrls: ['agreementManagementList.css'],
  standalone: false
})
export class AgreementManagementList implements OnInit, OnDestroy {
  agreements: AgreementList[] = [];
  sequenceList: number[] = [];
  isLoading = true
  dialog = inject(Dialog);
  isAscending: boolean = false;
  sortColumn: keyof AgreementList | null = null;
  openActivate = false
  openDeactivate = false
  agreement_id = -1

  allAgreements$: Observable<AgreementList[]> =
    this.agreementService.listAllAgreements().pipe(
      map((agreements) =>
        agreements.map((agreement) => agreement as AgreementList)
      )
    );
  agreementsSub: Subscription = this.allAgreements$.subscribe(
    (agreements) => {
      this.isLoading = false;
      this.agreements = agreements;
      this.agreements.forEach((agreement) => {
        agreement.status = 'Inactive';
        if (agreement.effective_date !== null) {
          agreement.effective_date = new Date(agreement.effective_date).toLocaleDateString();
          agreement.status = 'Active';
        }
      })
    },
    () => {
      this.isLoading = false;
      this._snackBar.open('Unable to load agreements. Please try again.', 'Dismiss', { panelClass: ['error-snackbar'] })
    }
  );

  constructor(
    private router: Router,
    private agreementService: AgreementService,
    private _snackBar: MatSnackBar
  ) { }

  ngOnInit() { }

  ngOnDestroy() {
    this.agreementsSub.unsubscribe();
  }

  add() {
    this.router.navigate(['dashboard/AgreementManagement/add'])
  }

  edit(id: number) {
    this.router.navigate(['dashboard/AgreementManagement/edit'], { queryParams: { id: id } })
  }

  sort(column: keyof AgreementList) {
    this.isAscending = this.sortColumn === column ? !this.isAscending : true;
    this.sortColumn = column;
    this.agreements.sort((agreementA, agreementB) => {
      if (this.isAscending === true) {
        return agreementA[column] < agreementB[column] ? -1 : agreementA[column] > agreementB[column] ? 1 : 0;
      } else {
        return agreementA[column] > agreementB[column] ? -1 : agreementA[column] < agreementB[column] ? 1 : 0;
      }
    });
  }

  cancelDeactivate() {
    this.openDeactivate = false
    this.agreement_id = -1
  }

  confirmDeactivate(id: number) {
    this.agreementService.retireAgreement(id).subscribe((response) => {
      if (response) {
        window.location.reload()
        this._snackBar.open('Agreement successfully deactivate', 'Dismiss', { duration: 3000 })
        this.cancelDeactivate()
      } else {
        this._snackBar.open('Error deactivating agreement', 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
      }
    })
  }

  cancelActivate() {
    this.openActivate = false
    this.agreement_id = -1
  }

  confirmActivate(id: number) {
    this.agreementService.enableAgreement(id).subscribe((response) => {
      if (response) {
        this._snackBar.open('Agreement successfully activated', 'Dismiss', { duration: 3000 })
        this.agreements[_.indexOf(this.agreements, _.find(this.agreements, { agreement_id: id }))].status = 'Active'
        window.location.reload()
        this.cancelActivate()
      } else {
        this._snackBar.open('Error activating agreement', 'Dismiss', { duration: 5000, panelClass: ['error-snackbar'] })
      }
    })
  }

  activateToggle(id: number, effective_date?: string) {
    if (effective_date !== null) {
      this.openDeactivate = true
      this.agreement_id = id
    }
    else {
      this.openActivate = true
      this.agreement_id = id
    }
  }

  responses(id: number, title: string) {
    this.router.navigate(['dashboard/AgreementManagement/responses'], { queryParams: { title: title, id: id } })
  }

  protected readonly faAdd = faAdd;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faEye = faEye;
  protected readonly faPencilAlt = faPencilAlt;
}
